import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { Aurora } from "@/components/Aurora";
import { PadlockIcon } from "@/components/icons/PadlockIcon";

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoginSuccess, setIsLoginSuccess] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    setIsSubmitting(true);
    try {
      await login(email, password);
      setIsLoginSuccess(true);
      setTimeout(() => {
        navigate("/");
      }, 1000); // 1-second delay for animation
    } catch (err) {
      setError("Invalid email or password");
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Aurora
        colorStops={["#6be600", "#d5ff7a", "#FFFFFF"]}
        blend={0.75}
        amplitude={0.8}
        speed={0.6}
      >
        <div className="min-h-screen flex items-center justify-center p-8">
          <div className="w-full max-w-md space-y-4 md:space-y-16">
            <Card className="bg-transparent shadow-none border-none">
              <div className="flex justify-center py-8">
                <img src={logo} alt="Logo" className="h-16" />
              </div>
            </Card>

            <div className="relative">
              <div className="absolute -top-9 left-1/2 -translate-x-1/2 transform">
                <PadlockIcon
                  isUnlocked={isLoginSuccess}
                  className={`h-16 w-16 transition-transform duration-800 ease-in-out ${isLoginSuccess ? "scale-110" : ""}`}
                />
              </div>
              <Card className="bg-white border-none shadow-card-around pt-12">
                <CardHeader>
                  <CardTitle className="text-xl md:text-3xl font-bold text-center">
                    {t("common.login")}
                  </CardTitle>
                  {/* <CardDescription className="text-center">
                    {t("common.login-description")}
                  </CardDescription> */}
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <label htmlFor="email">Email</label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="password">{t("common.password")}</label>
                      <Input
                        id="password"
                        type="password"
                        placeholder={t("common.password")}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    {error && <p className="text-red-500 text-sm">{error}</p>}

                    <div className="text-left mt-4">
                      <Link
                        to="/retrieve"
                        className="text-black hover:underline"
                      >
                        {t("common.retrieve")}
                      </Link>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      variant="default"
                      className="w-full bg-primary text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                    >
                      {t("common.login-button")}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </Aurora>
    </>
  );
}
